import type { SVGProps } from 'react';

export type PrototypeIconName =
  | 'arrow-up'
  | 'bell'
  | 'book-open'
  | 'briefcase-business'
  | 'chart-column'
  | 'chevron-down'
  | 'circle-help'
  | 'circle-alert'
  | 'coins'
  | 'piggy-bank'
  | 'wallet'
  | 'cloud-upload'
  | 'plus'
  | 'pencil'
  | 'refresh-cw'
  | 'palette'
  | 'check'
  | 'send'
  | 'database'
  | 'eye'
  | 'file-text'
  | 'folder-kanban'
  | 'hammer'
  | 'history'
  | 'layout-grid'
  | 'life-buoy'
  | 'network'
  | 'search'
  | 'settings'
  | 'shield-check'
  | 'sparkles'
  | 'users-round'
  | 'user-round-help'
  | 'arrow-right'
  | 'arrow-left'
  | 'x';

interface PrototypeIconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  readonly name: PrototypeIconName;
}

/**
 * Sous-ensemble des pictogrammes Lucide utilisés par le prototype v22.
 *
 * Les tracés proviennent des SVG officiels Lucide (licence ISC). Les garder ici évite une
 * dépendance front complète pour une quinzaine de glyphes tout en garantissant que Tosho ne
 * réinvente pas des pictogrammes « proches » de ceux validés pendant la recette.
 */
export function PrototypeIcon({ name, ...props }: PrototypeIconProps): React.JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      data-icon={name}
      {...props}
    >
      {iconContent(name)}
    </svg>
  );
}

function iconContent(name: PrototypeIconName): React.JSX.Element | React.JSX.Element[] {
  switch (name) {
    case 'plus':
      return <path d="M12 5v14M5 12h14" />;
    case 'pencil':
      return [<path key="body" d="m15 5 4 4M4 20l4.5-1 11-11a2.12 2.12 0 0 0-3-3l-11 11L4 20Z" />];
    case 'refresh-cw':
      return [
        <path key="top" d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />,
        <path key="top-arrow" d="M21 3v5h-5" />,
        <path key="bottom" d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />,
        <path key="bottom-arrow" d="M8 16H3v5" />,
      ];
    case 'palette':
      return [
        <path
          key="outline"
          d="M12 22a10 10 0 1 1 10-10 3 3 0 0 1-3 3h-1.8a2 2 0 0 0-1.6 3.2A2.4 2.4 0 0 1 12 22Z"
        />,
        <circle key="one" cx="7.5" cy="11" r=".5" />,
        <circle key="two" cx="10" cy="7" r=".5" />,
        <circle key="three" cx="15" cy="7.5" r=".5" />,
        <circle key="four" cx="17" cy="11" r=".5" />,
      ];
    case 'check':
      return <path d="m20 6-11 11-5-5" />;
    case 'send':
      return [<path key="body" d="m22 2-7 20-4-9-9-4Z" />, <path key="line" d="M22 2 11 13" />];
    case 'cloud-upload':
      return [
        <path key="cloud" d="M20 16.6A4.5 4.5 0 0 0 18 8a6 6 0 0 0-11.4 1.8A4 4 0 0 0 6 18" />,
        <path key="arrow" d="m12 20 0-8m-3 3 3-3 3 3" />,
      ];
    case 'arrow-up':
      return [<path key="p1" d="m5 12 7-7 7 7" />, <path key="p2" d="M12 19V5" />];
    case 'arrow-right':
      return [<path key="p1" d="m12 5 7 7-7 7" />, <path key="p2" d="M5 12h14" />];
    case 'arrow-left':
      return [<path key="p1" d="m12 19-7-7 7-7" />, <path key="p2" d="M19 12H5" />];
    case 'search':
      return [<path key="p" d="m21 21-4.34-4.34" />, <circle key="c" cx="11" cy="11" r="8" />];
    case 'bell':
      return [
        <path key="p1" d="M10.268 21a2 2 0 0 0 3.464 0" />,
        <path
          key="p2"
          d="M3.262 15.326A1 1 0 0 0 4 17h16a1 1 0 0 0 .74-1.673C19.41 13.956 18 12.499 18 8A6 6 0 0 0 6 8c0 4.499-1.411 5.956-2.738 7.326"
        />,
      ];
    case 'chevron-down':
      return <path d="m6 9 6 6 6-6" />;
    case 'circle-alert':
      return (
        <>
          <circle cx="12" cy="12" r="10" />
          <path d="M12 8v4M12 16h.01" />
        </>
      );
    case 'circle-help':
      return [
        <circle key="c" cx="12" cy="12" r="10" />,
        <path key="p1" d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />,
        <path key="p2" d="M12 17h.01" />,
      ];
    case 'layout-grid':
      return [
        <rect key="r1" width="7" height="7" x="3" y="3" rx="1" />,
        <rect key="r2" width="7" height="7" x="14" y="3" rx="1" />,
        <rect key="r3" width="7" height="7" x="14" y="14" rx="1" />,
        <rect key="r4" width="7" height="7" x="3" y="14" rx="1" />,
      ];
    case 'users-round':
      return [
        <path key="p1" d="M18 21a8 8 0 0 0-16 0" />,
        <circle key="c" cx="10" cy="8" r="5" />,
        <path key="p2" d="M22 20c0-3.37-2-6.5-4-8a5 5 0 0 0-.45-8.3" />,
      ];
    case 'user-round-help':
      return [
        <circle key="head" cx="9" cy="8" r="4" />,
        <path key="body" d="M2 21a7 7 0 0 1 12-5" />,
        <path key="question" d="M17 16a2 2 0 1 1 2 2c-.7.4-1 1-1 1.5" />,
        <path key="dot" d="M18 22h.01" />,
      ];
    case 'folder-kanban':
      return [
        <path
          key="p1"
          d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.93a2 2 0 0 1-1.66-.9l-.82-1.2A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z"
        />,
        <path key="p2" d="M8 10v4" />,
        <path key="p3" d="M12 10v2" />,
        <path key="p4" d="M16 10v6" />,
      ];
    case 'life-buoy':
      return [
        <circle key="c1" cx="12" cy="12" r="10" />,
        <path key="p1" d="m4.93 4.93 4.24 4.24" />,
        <path key="p2" d="m14.83 9.17 4.24-4.24" />,
        <path key="p3" d="m14.83 14.83 4.24 4.24" />,
        <path key="p4" d="m9.17 14.83-4.24 4.24" />,
        <circle key="c2" cx="12" cy="12" r="4" />,
      ];
    case 'book-open':
      return [
        <path key="p1" d="M12 5v16" />,
        <path
          key="p2"
          d="M20.001 19A2 2 0 0022 17V5a2 2 0 00-1.999-2L16 3.002A5 5 0 0012 5a5 5 0 00-4-2H4a2 2 0 00-2 2v12a2 2 0 001.999 2H8a5 5 0 014 2 5 5 0 014-2z"
        />,
      ];
    case 'coins':
      return [
        <circle key="c1" cx="8" cy="8" r="6" />,
        <path key="p1" d="M18.09 10.37A6 6 0 1 1 10.34 18" />,
        <path key="p2" d="M7 6h1v4" />,
        <path key="p3" d="m16.71 13.88.7.71-2.82 2.82" />,
      ];
    case 'database':
      return [
        <ellipse key="e" cx="12" cy="5" rx="9" ry="3" />,
        <path key="p1" d="M3 5V19A9 3 0 0 0 21 19V5" />,
        <path key="p2" d="M3 12A9 3 0 0 0 21 12" />,
      ];
    case 'eye':
      return [
        <path
          key="outline"
          d="M2.06 12.35a1 1 0 0 1 0-.7 10.8 10.8 0 0 1 19.88 0 1 1 0 0 1 0 .7 10.8 10.8 0 0 1-19.88 0Z"
        />,
        <circle key="pupil" cx="12" cy="12" r="3" />,
      ];
    case 'file-text':
      return [
        <path key="p1" d="M14.5 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7.5L14.5 2z" />,
        <polyline key="p2" points="14 2 14 8 20 8" />,
        <path key="p3" d="M16 13H8" />,
        <path key="p4" d="M16 17H8" />,
        <path key="p5" d="M10 9H8" />,
      ];
    case 'sparkles':
      return [
        <path
          key="p1"
          d="M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z"
        />,
        <path key="p2" d="M20 2v4" />,
        <path key="p3" d="M22 4h-4" />,
        <circle key="c" cx="4" cy="20" r="2" />,
      ];
    case 'briefcase-business':
      return [
        <path key="p1" d="M12 12h.01" />,
        <path key="p2" d="M16 6V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" />,
        <path key="p3" d="M22 13a18.15 18.15 0 0 1-20 0" />,
        <rect key="r" width="20" height="14" x="2" y="6" rx="2" />,
      ];
    case 'chart-column':
      return [
        <path key="p1" d="M3 3v16a2 2 0 0 0 2 2h16" />,
        <path key="p2" d="M18 17V9" />,
        <path key="p3" d="M13 17V5" />,
        <path key="p4" d="M8 17v-3" />,
      ];
    case 'network':
      return [
        <rect key="r1" x="16" y="16" width="6" height="6" rx="1" />,
        <rect key="r2" x="2" y="16" width="6" height="6" rx="1" />,
        <rect key="r3" x="9" y="2" width="6" height="6" rx="1" />,
        <path key="p1" d="M5 16v-3a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3" />,
        <path key="p2" d="M12 12V8" />,
      ];
    case 'settings':
      return [
        <path
          key="p"
          d="M9.671 4.136a2.34 2.34 0 0 1 4.659 0 2.34 2.34 0 0 0 3.319 1.915 2.34 2.34 0 0 1 2.33 4.033 2.34 2.34 0 0 0 0 3.831 2.34 2.34 0 0 1-2.33 4.033 2.34 2.34 0 0 0-3.319 1.915 2.34 2.34 0 0 1-4.659 0 2.34 2.34 0 0 0-3.32-1.915 2.34 2.34 0 0 1-2.33-4.033 2.34 2.34 0 0 0 0-3.831A2.34 2.34 0 0 1 6.35 6.051a2.34 2.34 0 0 0 3.319-1.915"
        />,
        <circle key="c" cx="12" cy="12" r="3" />,
      ];
    case 'shield-check':
      return [
        <path
          key="p1"
          d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
        />,
        <path key="p2" d="m9 12 2 2 4-4" />,
      ];
    case 'history':
      return [
        <path key="p1" d="M3 12a9 9 0 1 0 3-6.7L3 8" />,
        <path key="p2" d="M3 3v5h5" />,
        <path key="p3" d="M12 7v5l3 2" />,
      ];
    case 'hammer':
      return [
        <path key="p1" d="m15 12-9.373 9.373a1 1 0 0 1-3.001-3L12 9" />,
        <path key="p2" d="m18 15 4-4" />,
        <path
          key="p3"
          d="m21.5 11.5-1.914-1.914A2 2 0 0 1 19 8.172v-.344a2 2 0 0 0-.586-1.414l-1.657-1.657A6 6 0 0 0 12.516 3H9l1.243 1.243A6 6 0 0 1 12 8.485V10l2 2h1.172a2 2 0 0 1 1.414.586L18.5 14.5"
        />,
      ];
    case 'piggy-bank':
      return [
        <path
          key="p1"
          d="M11 17h3v2a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1v-3a3.16 3.16 0 0 0 2-2h1a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1h-1a5 5 0 0 0-2-4V3a4 4 0 0 0-3.2 1.6l-.3.4H11a6 6 0 0 0-6 6v1a5 5 0 0 0 2 4v3a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1z"
        />,
        <path key="p2" d="M16 10h.01" />,
        <path key="p3" d="M2 8v1a2 2 0 0 0 2 2h1" />,
      ];
    case 'wallet':
      return [
        <path
          key="p1"
          d="M19 7V4a1 1 0 0 0-1-1H5a2 2 0 0 0 0 4h15a1 1 0 0 1 1 1v4h-3a2 2 0 0 0 0 4h3a1 1 0 0 0 1-1v-2a1 1 0 0 0-1-1"
        />,
        <path key="p2" d="M3 5v14a2 2 0 0 0 2 2h15a1 1 0 0 0 1-1v-4" />,
      ];
    case 'x':
      return [<path key="p1" d="M18 6 6 18" />, <path key="p2" d="m6 6 12 12" />];
  }
}
